import * as React from "react";
import { cn } from "../_internal/cn";
import NarrativeAttribution from "../NarrativeAttribution/NarrativeAttribution";
import type { TypeNarrativeSocialMediaCardProps } from "./NarrativeSocialMediaCardTypes";

/**
 * NarrativeSocialMediaCard — a narrative primitive that displays a social media
 * post card with a header (avatar + handle), content area, and footer with
 * engagement metadata (replies, likes, reposts, etc.).
 *
 * Styled via narrative-social-media-card.css (Seeds CSS custom properties);
 * consumers import the classes through @sproutsocial/racine/css/components.
 * Layout is vertically stacked with clean typography and icon-based metadata in
 * the footer. The card fills the width it is given (e.g. a grid column) and
 * resizes with it — like the horizontal NarrativeOrderedList — so a row of cards
 * grows and stretches together rather than being pinned to a fixed width.
 * Default has a gray background; use appearance="no-background" to remove the
 * background and blend seamlessly with the page.
 */
const NarrativeSocialMediaCard = React.forwardRef<
  HTMLDivElement,
  TypeNarrativeSocialMediaCardProps
>(
  (
    {
      avatar,
      avatarAlt,
      displayName,
      socialIcon,
      image,
      text,
      meta,
      appearance,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          "seeds-narrative-social-media-card",
          appearance === "no-background" &&
            "seeds-narrative-social-media-card--no-background",
          className
        )}
        {...props}
      >
        <NarrativeAttribution
          avatarUrl={avatar}
          avatarAlt={avatarAlt}
          userName={displayName}
          socialIcon={socialIcon}
        />

        <div className="seeds-narrative-social-media-card-content">
          {image}
          {text}
        </div>

        <div className="seeds-narrative-social-media-card-footer">
          {meta.map((item, index) => (
            <div
              key={index}
              className="seeds-narrative-social-media-card-stat"
              aria-label={item.label}
            >
              {item.icon}
              <span className="seeds-narrative-social-media-card-stat-value">
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

NarrativeSocialMediaCard.displayName = "NarrativeSocialMediaCard";

export default NarrativeSocialMediaCard;
